import React, { useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  Grid,
  HStack,
  Input,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react';
import { DESIGN_PRESETS } from '../data/designPresets';

const FieldLabel = ({ children }) => (
  <Text fontSize="xs" color="gray.400" mb={1}>{children}</Text>
);

const ColorSwatch = ({ value, onChange, disabled }) => (
  <Box
    as="label"
    w={7}
    h={7}
    borderRadius="md"
    borderWidth="1px"
    borderColor="gray.200"
    overflow="hidden"
    cursor={disabled ? 'not-allowed' : 'pointer'}
    flexShrink={0}
    opacity={disabled ? 0.4 : 1}
    title={value}
  >
    <Input
      type="color"
      value={value}
      onChange={onChange}
      w="200%"
      h="200%"
      mt="-25%"
      ml="-25%"
      border="none"
      p={0}
      cursor="pointer"
      isDisabled={disabled}
    />
  </Box>
);

const QrDesignPanel = observer(({ store }) => {
  const fileInputRef = useRef(null);
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => store.setDesign({ logoUrl: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="gray.400">
          Design
        </Text>
        <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => store.resetDesign()}>
          Reset
        </Button>
      </Flex>

      {/* Row 1 — Presets + Colors */}
      <Flex gap={4} align="flex-start" mb={4}>
        {/* Presets */}
        <Box flex="1" minW={0}>
          <FieldLabel>Presets</FieldLabel>
          <Flex gap={1} flexWrap="wrap">
            {Object.values(DESIGN_PRESETS).map((p) => (
              <Button key={p.id} size="xs" variant="outline" onClick={() => store.applyPreset(p.id)}>
                {p.label}
              </Button>
            ))}
          </Flex>
        </Box>

        {/* Colors */}
        <Box flexShrink={0}>
          <FieldLabel>Colors</FieldLabel>
          <HStack spacing={2}>
            <Flex direction="column" align="center" gap={1}>
              <ColorSwatch
                value={store.design.fgColor}
                onChange={(e) => store.setDesign({ fgColor: e.target.value })}
              />
              <Text fontSize="9px" color={mutedColor}>QR</Text>
            </Flex>
            <Flex direction="column" align="center" gap={1}>
              <ColorSwatch
                value={store.design.bgColor}
                onChange={(e) => store.setDesign({ bgColor: e.target.value })}
                disabled={store.design.transparentBg}
              />
              <Text fontSize="9px" color={mutedColor}>BG</Text>
            </Flex>
            <Checkbox
              size="sm"
              isChecked={store.design.transparentBg}
              onChange={(e) => store.setDesign({ transparentBg: e.target.checked })}
              colorScheme="blue"
              title="Transparent background"
              alignSelf="center"
              mt={1}
            />
          </HStack>
        </Box>
      </Flex>

      {/* Row 2 — Dots/Corners/Error (50%) + Margin (50%) */}
      <Flex gap={3} mb={4} align="flex-end">
        {/* Left 60% — 3 compact selects */}
        <Grid templateColumns="1fr 1fr 1fr" gap={2} flex="1.5">
          <FormControl>
            <FieldLabel>Dots</FieldLabel>
            <Select value={store.design.dotStyle} onChange={(e) => store.setDesign({ dotStyle: e.target.value })} size="xs">
              <option value="square">Square</option>
              <option value="rounded">Rounded</option>
              <option value="dot">Dot</option>
              <option value="extra-rounded">Extra rnd.</option>
            </Select>
          </FormControl>
          <FormControl>
            <FieldLabel>Corners</FieldLabel>
            <Select value={store.design.cornerStyle} onChange={(e) => store.setDesign({ cornerStyle: e.target.value })} size="xs">
              <option value="square">Square</option>
              <option value="rounded">Rounded</option>
              <option value="dot">Dot</option>
              <option value="extra-rounded">Extra rnd.</option>
            </Select>
          </FormControl>
          <FormControl>
            <FieldLabel>Err. corr.</FieldLabel>
            <Select value={store.design.errorCorrection} onChange={(e) => store.setDesign({ errorCorrection: e.target.value })} size="xs">
              <option value="L">L — 7%</option>
              <option value="M">M — 15%</option>
              <option value="Q">Q — 25%</option>
              <option value="H">H — 30%</option>
            </Select>
          </FormControl>
        </Grid>

        {/* Right 40% — Margin slider */}
        <FormControl flex="1">
          <Flex justify="space-between" align="baseline" mb={1}>
            <FieldLabel>Margin</FieldLabel>
            <Text fontSize="xs" color={mutedColor}>{store.design.margin}px</Text>
          </Flex>
          <Slider value={store.design.margin} min={0} max={30} onChange={(v) => store.setDesign({ margin: v })}>
            <SliderTrack bg="gray.200"><SliderFilledTrack bg="blue.400" /></SliderTrack>
            <SliderThumb boxSize={3} />
          </Slider>
        </FormControl>
      </Flex>

      {/* Row 4 — Logo */}
      <Box>
        <FieldLabel>Logo</FieldLabel>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
        {store.design.logoUrl ? (
          <Box>
            <Flex align="center" gap={3} mb={2}>
              <Box w={8} h={8} borderRadius="md" overflow="hidden" borderWidth="1px" borderColor="gray.200" flexShrink={0}>
                <img src={store.design.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </Box>
              <Button size="xs" variant="ghost" colorScheme="red"
                onClick={() => { store.setDesign({ logoUrl: null }); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                Remove
              </Button>
            </Flex>
            <Flex justify="space-between" align="baseline" mb={1}>
              <FieldLabel>Size</FieldLabel>
              <Text fontSize="xs" color={mutedColor}>{Math.round(store.design.logoSize * 100)}%</Text>
            </Flex>
            <Slider value={store.design.logoSize * 100} min={10} max={50} onChange={(v) => store.setDesign({ logoSize: v / 100 })}>
              <SliderTrack bg="gray.200"><SliderFilledTrack bg="blue.400" /></SliderTrack>
              <SliderThumb boxSize={3} />
            </Slider>
          </Box>
        ) : (
          <Button size="sm" variant="outline" w="full" onClick={() => fileInputRef.current?.click()}>
            Upload logo
          </Button>
        )}
      </Box>
    </Box>
  );
});

export default QrDesignPanel;
